import RelativeTime from "components/ui/RelativeTime";
import { LinkButton } from "lib/miolo";
import Link from "next/link";
import * as React from "react";
import { useState } from "react";
import type { IAssetDiscussionCommentFieldsFragment } from "../../lib/graphql/fragments/__generated__/AssetDiscussionCommentFields.generated";
import type { IAssetDiscussionCommentParticipantsFieldsFragment } from "../../lib/graphql/fragments/__generated__/AssetDiscussionCommentParticipantsFields.generated";
import type { IAssetDiscussionCommentRepliesPaginationFieldsFragment } from "../../lib/graphql/fragments/__generated__/AssetDiscussionCommentRepliesPaginationFields.generated";
import useSelfie from "../../lib/hooks/useSelfie";
import translateTypenameIntoAssetType from "../../lib/utils/translateTypenameIntoAssetType";
import { IReactionSubject } from "../../types/graphqlTypes";
import Avatar from "../core/Avatar";
import { Menu, MenuActionButtonItem, MenuDivider, MenuTitle } from "../core/menu";
import * as palette from "../palette";
import TextTooltip from "../shared/TextTooltip";
import AssetDiscussionCommentParticipants from "./AssetDiscussionCommentParticipants";
import AssetDiscussionCommentReply from "./AssetDiscussionCommentReply";
import CommentActionsDropdown from "./CommentActionsDropdown";
import CommentContent from "./CommentContent";
import IndividualCommentInputForm from "./form/IndividualCommentInputForm";
import ReplyCommentInputForm from "./form/ReplyCommentInputForm";
import translateServerErrorIntoFriendlyMessage from "./form/translateServerErrorIntoFriendlyMessage";
import useCommentActions from "./hooks/useCommentActions";
import HorizontalLineDivider from "./HorizontalLineDivider";
import InlineAssetDiscussionCommentError from "./InlineAssetDiscussionCommentError";
import ReplyToAssetDiscussionCommentMutation from "./mutations/ReplyToAssetDiscussionCommentMutation";
import ThumbsUpReaction from "./reactions/ThumbsUpReaction";
import ViewMoreReplies from "./ViewMoreReplies";

interface IAssetDiscussionCommentProps {
  comment: IAssetDiscussionCommentFieldsFragment;
  participants?: IAssetDiscussionCommentParticipantsFieldsFragment;
  replies?: IAssetDiscussionCommentRepliesPaginationFieldsFragment;
  allowToReplyRootComment?: boolean;
  allowToReplyIndividualReplies?: boolean;
  shouldDisplayReplies?: boolean;
}

export default function AssetDiscussionComment({
  comment,
  participants,
  replies,
  allowToReplyRootComment = true,
  allowToReplyIndividualReplies = true,
  shouldDisplayReplies = false,
}: IAssetDiscussionCommentProps) {
  const selfie = useSelfie();

  const [isEditingContent, setIsEditingContent] = useState<boolean>(false);
  const [isWritingReply, setIsWritingReply] = useState<boolean>(false);
  const [isShowingReplies, setIsShowingReplies] = useState<boolean>(shouldDisplayReplies);

  const {
    destroyComment,
    editComment,
    lockComment,
    unlockComment,
    pinComment,
    unpinComment,

    isDestroyingComment,
    isLockingComment,
    isUnlockingComment,
    isPinningComment,
    isUnpinningComment,

    destroyCommentError,
    editCommentError,
    lockCommentError,
    unlockCommentError,
    pinCommentError,
    unpinCommentError,
  } = useCommentActions(comment);

  const viewerCanTakeActions = comment.viewerCanEdit || comment.viewerCanDestroy || (selfie && selfie.isSiteAdmin);

  return (
    <div
      // NOTE: using `display: grid` here causes some painful layout reflow/thrashing because of the resizeable textarea
      // (CommentInputTextArea). Do not use the following code:
      // css={{
      //   display: "grid",
      //   gridColumnGap: 16,
      //   gridTemplateColumns: "42px 1fr",
      //   boxSizing: "border-box",
      // }}
      className="font-aeonik font-normal"
      css={{
        display: "flex",
        boxSizing: "border-box",
        "& > div:first-of-type": {
          marginRight: 16,
        },
      }}
    >
      <div className="relative">
        <Avatar
          src={comment.author.pictureUrl}
          hashValue={comment.author._id}
          alt={comment.author.fullName}
          squircle={true}
          variant="rounded"
          size={42}
        />

        {isShowingReplies && replies && replies.nodes.length > 0 && (
          <div
            className="bg-gray-100 dark:bg-gray-700"
            css={{
              position: "absolute",
              left: "calc(50% - 2px)",
              width: 2,
              top: 42,
              marginTop: 16,
              height: "calc(100% - 42px - 16px)",
              content: '""',
            }}
          />
        )}
      </div>

      <div
        css={{
          width: "100%",
          "& > div, & > hr, & > button": {
            marginTop: 16,
          },
        }}
      >
        {comment.isLocked && (
          <div
            css={{
              display: "grid",
              gridColumnGap: 8,
              gridTemplateColumns: "max-content 1fr",
              alignItems: "center",
              color: palette.fiord,
            }}
            style={{
              marginTop: 0,
              marginBottom: 8,
            }}
          >
            <LockIcon />

            <span
              css={{
                fontWeight: 400,
                fontSize: 14,
                lineHeight: 1.33,
                letterSpacing: 0.8,
              }}
            >
              Comentário trancado
            </span>
          </div>
        )}

        {comment.isPinned && comment.pinnedBy && (
          <div
            css={{
              display: "grid",
              gridColumnGap: 8,
              gridTemplateColumns: "max-content 1fr",
              alignItems: "center",
              color: palette.fiord,
            }}
            style={{
              marginTop: 0,
              marginBottom: 8,
            }}
          >
            <ThumbtackIcon />

            <span
              css={{
                fontWeight: 400,
                fontSize: 14,
                lineHeight: 1.33,
                letterSpacing: 0.8,
              }}
            >
              Comentário fixado
            </span>
          </div>
        )}

        <div style={{ marginTop: 0 }} className="mt-0 flex flex-col">
          <strong className="text-sm dark:text-slate-400">{comment.author.fullName}</strong>

          <div>
            <Link
              href="/comments/[shortId]"
              as={`/comments/${comment.shortId}`}
              passHref={true}
              className="inline-flex hover:underline"
            >
              <RelativeTime
                className="text-xs font-medium text-slate-500 dark:text-gray-500 md:text-sm"
                timeStamp={comment.postedAt}
              />
            </Link>

            {comment.lastEditedAt && (
              <span className="text-xs font-medium text-slate-500 md:text-sm">{" • editado"}</span>
            )}
          </div>
        </div>

        {isEditingContent ? (
          <>
            <IndividualCommentInputForm
              submitButtonText="Editar"
              onSubmit={async (bodyAsPlainText) => {
                await editComment(bodyAsPlainText);
                setIsEditingContent(false);
              }}
              onCancel={() => setIsEditingContent(false)}
              bodyAsPlainText={comment.bodyAsPlainText}
            />

            <HorizontalLineDivider />
          </>
        ) : (
          <CommentContent bodyAsPlainText={comment.bodyAsPlainText} />
        )}

        <ThumbsUpReaction
          subjectId={comment._id}
          subjectType={IReactionSubject.AssetDiscussionComment}
          reactable={comment}
        />

        <div
          css={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
          }}
        >
          <div
            css={{
              display: "grid",
              gridAutoColumns: "max-content",
              gridAutoFlow: "column",
              gridColumnGap: 12,
            }}
          >
            {comment.viewerCanReply && allowToReplyRootComment && replies && replies.totalCount > 0 && (
              <LinkButton
                variant="primary"
                onClick={() => {
                  if (isShowingReplies && isWritingReply) {
                    setIsWritingReply((previous) => !previous);
                  }

                  setIsShowingReplies((previous) => !previous);
                }}
              >
                {!isShowingReplies ? `Ver respostas (${replies.totalCount})` : "Ocultar respostas"}
              </LinkButton>
            )}

            {comment.viewerCanReply &&
              allowToReplyRootComment &&
              ((replies && replies.totalCount === 0) || isShowingReplies) && (
                <LinkButton
                  variant="primary"
                  onClick={() => {
                    setIsWritingReply(!isWritingReply);
                  }}
                >
                  Responder
                </LinkButton>
              )}

            {!comment.viewerCanReply && replies && replies.totalCount > 0 && (
              <TextTooltip
                id={`tooltips/mustBePremiumToViewReplies/${comment.shortId}`}
                placement="top"
                content="Você precisa ser Premium para ver as respostas"
              >
                {({ accessibility, bind, ref }) => {
                  return (
                    <span
                      {...accessibility}
                      ref={ref}
                      {...bind}
                      css={{
                        color: palette.royalBlue,
                        fontWeight: 500,
                        fontSize: 14,
                        lineHeight: 1.33,
                        cursor: "default",
                        userSelect: "none",
                      }}
                    >
                      Respostas ({replies.totalCount})
                    </span>
                  );
                }}
              </TextTooltip>
            )}

            {viewerCanTakeActions && (
              <CommentActionsDropdown>
                {({ closeOnClick }) => (
                  <Menu>
                    {comment.viewerCanEdit && (
                      <MenuActionButtonItem
                        intent="primary"
                        onClick={closeOnClick(() => setIsEditingContent(!isEditingContent))}
                      >
                        Editar
                      </MenuActionButtonItem>
                    )}

                    {selfie && selfie.isSiteAdmin && (
                      <>
                        <MenuActionButtonItem
                          intent="primary"
                          onClick={closeOnClick(() => {
                            navigator.clipboard.writeText(comment.author.email);
                          })}
                        >
                          Copiar email
                        </MenuActionButtonItem>

                        <MenuDivider />

                        <MenuTitle>Moderação</MenuTitle>

                        {comment.isPinned ? (
                          <MenuActionButtonItem disabled={isUnpinningComment} onClick={closeOnClick(unpinComment)}>
                            {isUnpinningComment ? "Desfixando..." : "Desfixar"}
                          </MenuActionButtonItem>
                        ) : (
                          <MenuActionButtonItem disabled={isPinningComment} onClick={closeOnClick(pinComment)}>
                            {isPinningComment ? "Fixando..." : "Fixar"}
                          </MenuActionButtonItem>
                        )}

                        {comment.isLocked ? (
                          <MenuActionButtonItem disabled={isUnlockingComment} onClick={closeOnClick(unlockComment)}>
                            {isUnlockingComment ? "Destrancando..." : "Destrancar"}
                          </MenuActionButtonItem>
                        ) : (
                          <MenuActionButtonItem disabled={isLockingComment} onClick={closeOnClick(lockComment)}>
                            {isLockingComment ? "Trancando..." : "Trancar"}
                          </MenuActionButtonItem>
                        )}
                      </>
                    )}

                    <MenuDivider />

                    {comment.viewerCanDestroy && (
                      <MenuActionButtonItem
                        intent="danger"
                        disabled={isDestroyingComment}
                        onClick={() => destroyComment()}
                      >
                        {isDestroyingComment ? "Apagando..." : "Apagar"}
                      </MenuActionButtonItem>
                    )}
                  </Menu>
                )}
              </CommentActionsDropdown>
            )}

            {participants && participants.nodes.length > 0 && (
              <div>
                <AssetDiscussionCommentParticipants
                  participants={participants.nodes}
                  totalCount={participants.totalCount}
                />
              </div>
            )}
          </div>
        </div>

        {selfie && isWritingReply && (
          <>
            <HorizontalLineDivider />

            <ReplyToAssetDiscussionCommentMutation>
              {(replyToAssetDiscussionComment) => (
                <div>
                  <ReplyCommentInputForm
                    viewer={selfie}
                    autoFocus={true}
                    onCancel={() => setIsWritingReply(false)}
                    onSubmit={async (bodyAsPlainText) => {
                      await replyToAssetDiscussionComment({
                        assetId: comment.asset._id,
                        assetType: translateTypenameIntoAssetType(comment.asset.__typename),
                        replyToCommentId: comment._id,
                        replyToCommentShortId: comment.shortId,
                        bodyAsPlainText,
                      });

                      setIsWritingReply(false);
                    }}
                  />
                </div>
              )}
            </ReplyToAssetDiscussionCommentMutation>

            {replies && replies.totalCount > 0 && <HorizontalLineDivider />}
          </>
        )}

        {firstError([
          destroyCommentError,
          editCommentError,
          lockCommentError,
          unlockCommentError,
          pinCommentError,
          unpinCommentError,
        ]) && (
          <div>
            <InlineAssetDiscussionCommentError>
              {translateServerErrorIntoFriendlyMessage(
                firstError([
                  destroyCommentError,
                  editCommentError,
                  lockCommentError,
                  unlockCommentError,
                  pinCommentError,
                  unpinCommentError,
                ]) || new Error("Ih! Tem algo errado...tente novamente mais tarde."),
              )}
            </InlineAssetDiscussionCommentError>
          </div>
        )}

        {isShowingReplies && replies && (
          <>
            <HorizontalLineDivider />

            {replies.nodes.length > 0 && (
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {replies.nodes.map((reply) => {
                  return (
                    <div key={reply.shortId} className="py-4 first:pt-0 last:pb-0">
                      <AssetDiscussionCommentReply
                        reply={reply}
                        rootComment={comment}
                        allowToReply={allowToReplyIndividualReplies}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {replies.pageInfo.hasNextPage && (
              <ViewMoreReplies assetId={comment.asset._id} rootCommentShortId={comment.shortId} repliesPerPage={8} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function firstError(arrayOfErrors: (Error | null)[]): Error | null {
  return arrayOfErrors.find((error) => error !== null) || null;
}

function ThumbtackIcon(props: React.SVGProps<any>) {
  return (
    <svg aria-hidden="true" viewBox="0 0 384 512" width="1em" height="1em" {...props}>
      <path
        fill="currentColor"
        d="M300.8 203.9L290.7 128H328c13.2 0 24-10.8 24-24V24c0-13.2-10.8-24-24-24H56C42.8 0 32 10.8 32 24v80c0 13.2 10.8 24 24 24h37.3l-10.1 75.9C34.9 231.5 0 278.4 0 335.2c0 8.8 7.2 16 16 16h160V472c0 .7.1 1.3.2 1.9l8 32c2 8 13.5 8.1 15.5 0l8-32c.2-.6.2-1.3.2-1.9V351.2h160c8.8 0 16-7.2 16-16 .1-56.8-34.8-103.7-83.1-131.3zM33.3 319.2c6.8-42.9 39.6-76.4 79.5-94.5L128 96H64V32h256v64h-64l15.3 128.8c40 18.2 72.7 51.8 79.5 94.5H33.3z"
      />
    </svg>
  );
}

function LockIcon(props: React.SVGProps<any>) {
  return (
    <svg aria-hidden="true" width="1em" height="1em" viewBox="0 0 14 15" {...props}>
      <path
        d="M12.46 6.533h-.499V4.71C11.961 2.132 9.747.01 6.993 0c-2.757-.009-5 2.088-5 4.667v1.866h-.498C.67 6.533 0 7.16 0 7.933v5.6c0 .773.67 1.4 1.495 1.4H12.46c.825 0 1.495-.627 1.495-1.4v-5.6c0-.773-.67-1.4-1.495-1.4zM2.99 4.667c0-2.06 1.788-3.734 3.987-3.734 2.2 0 3.988 1.675 3.988 3.734v1.866H2.99V4.667zM12.46 14H1.495c-.274 0-.498-.21-.498-.467v-5.6c0-.256.224-.466.498-.466H12.46c.274 0 .498.21.498.466v5.6c0 .257-.224.467-.498.467z"
        fill="#394E66"
        fillRule="nonzero"
      />
    </svg>
  );
}
